import React, { useMemo, useState, useCallback, useEffect } from 'react';
import useUI from '../hooks/useUI';
import useData from '../hooks/useData';
import useApp from '../hooks/useApp';
import IconClose from '../icons/IconClose';
import IconHeart from '../icons/IconHeart';
import IconHeartFilled from '../icons/IconHeartFilled';
import IconArrowDown from '../icons/IconArrowDown';
import SlideToggle from 'react-slide-toggle';
import useRecord from '../hooks/useRecord';

const SearchPanel = () => {
	const { SearchPanel, toggleUI, toggleExhibitorInfo } = useUI();
	const { exhibitorInfo, toggleFavourite } = useData();
	const { isCHT, setSelectedBooth, setActiveFloor, setSelectedExhibitor, setSelectedProduct } = useApp();
	const [searchTerm, setSearchTerm] = useState('');
	const [categories, setCategories] = useState([]);
	const [showSearchResult, setShowSearchResult] = useState(false);
	const { makeRecord } = useRecord();

	useEffect(() => {
		if (!categories.length) {
			const list = [];

			exhibitorInfo.forEach((exhibitor) => {
				exhibitor.products.forEach((product) => {
					const categoryLevel1CHT = product['產品大分類'];
					const categoryLevel2CHT = product['產品中分類'];
					const categoryLevel3CHT = product['產品小分類'];
					const categoryLevel1ENG = product['產品大分類-英文'];
					const categoryLevel2ENG = product['產品中分類-英文'];
					const categoryLevel3ENG = product['產品小分類-英文'];

					if (!list.find((category) => category.nameCHT === categoryLevel1CHT)) {
						list.push({
							nameCHT: categoryLevel1CHT,
							nameENG: categoryLevel1ENG,
							level2: [],
						});
					}

					if (!list.find((category) => category.nameCHT === categoryLevel1CHT).level2.find((level2) => level2.nameCHT === categoryLevel2CHT)) {
						list.find((category) => category.nameCHT === categoryLevel1CHT).level2.push({
							nameCHT: categoryLevel2CHT,
							nameENG: categoryLevel2ENG,
							active: true,
							level3: [],
						});
					}

					if (!list.find((category) => category.nameCHT === categoryLevel1CHT).level2.find((level2) => level2.nameCHT === categoryLevel2CHT).level3.find((level3) => level3.nameCHT === categoryLevel3CHT)) {
						list.find((category) => category.nameCHT === categoryLevel1CHT).level2.find((level2) => level2.nameCHT === categoryLevel2CHT).level3.push({
							nameCHT: categoryLevel3CHT,
							nameENG: categoryLevel3ENG,
							active: true,
						});
					}
				});
			});

			if (isCHT) {
				list.forEach((category, index) => {
					if (!category.nameCHT) {
						list.splice(index, 1);
					}
				});

				list.forEach((category) => {
					category.level2.forEach((level2, index) => {
						if (!level2.nameCHT) {
							category.level2.splice(index, 1);
						}
					});
				});

				list.forEach((category) => {
					category.level2.forEach((level2) => {
						level2.level3.forEach((level3, index) => {
							if (!level3.nameCHT) {
								level2.level3.splice(index, 1);
							}
						});
					});
				});
			}

			setCategories(list);
		}
	}, [exhibitorInfo, categories, isCHT]);

	const toggleLevel2Check = useCallback(({ categoryIndex, level2Index }) => {
		const updatedCategories = [...categories];

		updatedCategories[categoryIndex].level2[level2Index].active = !updatedCategories[categoryIndex].level2[level2Index].active;

		updatedCategories[categoryIndex].level2[level2Index].level3.forEach((level3) => {
			level3.active = updatedCategories[categoryIndex].level2[level2Index].active;
		});

		setCategories(updatedCategories);
	}, [categories]);

	const toggleLevel3Check = useCallback(({ categoryIndex, level2Index, level3Index }) => {
		const updatedCategories = [...categories];

		updatedCategories[categoryIndex].level2[level2Index].level3[level3Index].active = !updatedCategories[categoryIndex].level2[level2Index].level3[level3Index].active;

		updatedCategories[categoryIndex].level2[level2Index].active = updatedCategories[categoryIndex].level2[level2Index].level3.some((level3) => level3.active);

		setCategories(updatedCategories);
	}, [categories]);

	const exhibitorsContainingSelectedCategories = useMemo(() => {
		const exhibitors = [];

		exhibitorInfo.forEach((exhibitor) => {
			let hasActiveProduct = false;
			exhibitor.products.forEach((product) => {
				if (
					categories.some((category) => {
						return category.level2.some((level2) => {
							if (level2.active && !level2.level3.length) return true;
							if (level2.active) {
								return level2.level3.some((level3) => {
									return level3.active && (level3.nameCHT === product['產品小分類']);
								}) && (level2.nameCHT === product['產品中分類']);
							}
							return false;
						}) && (category.nameCHT === product['產品大分類']);
					})
				) {
					hasActiveProduct = true;
				}
			});

			if (hasActiveProduct) exhibitors.push(exhibitor);
		});

		return exhibitors;
	}, [exhibitorInfo, categories]);

	const filteredExhibitorInfo = useMemo(() => {
		if (!searchTerm) return exhibitorsContainingSelectedCategories;
		if (searchTerm) return exhibitorsContainingSelectedCategories.filter((exhibitor) => {
			const term = searchTerm.toLowerCase();
			return exhibitor['廠商名稱-中文'].toLowerCase().includes(term) || exhibitor['攤位號碼'].toLowerCase().includes(term) || exhibitor['廠商名稱-英文'].toLowerCase().includes(term);
		});
	}, [exhibitorsContainingSelectedCategories, searchTerm]);

	const productsContainingSelectedCategories = useMemo(() => {
		const products = [];

		exhibitorInfo.forEach((exhibitor) => {
			exhibitor.products.forEach((product) => {
				if (
					categories.some((category) => {
						return category.level2.some((level2) => {
							if (level2.active && !level2.level3.length) return true;
							if (level2.active) {
								return level2.level3.some((level3) => {
									return level3.active && (level3.nameCHT === product['產品小分類']);
								}) && (level2.nameCHT === product['產品中分類']);
							}
							return false;
						}) && (category.nameCHT === product['產品大分類']);
					})
				) {
					products.push(product);
				}
			});
		});

		return products;
	}, [exhibitorInfo, categories]);

	const filteredProductInfo = useMemo(() => {
		if (!searchTerm) return productsContainingSelectedCategories;
		if (searchTerm) return productsContainingSelectedCategories.filter((product) => {
			const term = searchTerm.toLowerCase();
			return product['產品名稱-中文'].includes(term) || product['產品名稱-英文'].toLowerCase().includes(term);
		});
	}, [productsContainingSelectedCategories, searchTerm]);

	const closePanel = useCallback(() => {
		toggleUI('SearchPanel');
	}, [toggleUI]);

	const toggleFavouriteExhibitor = useCallback((boothNumber) => {
		toggleFavourite(boothNumber);
	}, [toggleFavourite]);

	const chooseExhibitor = useCallback((boothNumber, floor) => {
		if (floor !== undefined) setActiveFloor(floor);
		setSelectedBooth(boothNumber);
		setSelectedExhibitor(boothNumber);
		toggleExhibitorInfo();
	}, [setActiveFloor, setSelectedBooth, setSelectedExhibitor, toggleExhibitorInfo]);

	const [searchType, setSearchType] = useState('company');

	const placeholder = useMemo(() => {
		if (searchType === 'company') return isCHT ? '搜尋廠商名稱' : 'Search company name';
		if (searchType === 'product') return isCHT ? '搜尋產品名稱' : 'Search product name';
	}, [isCHT, searchType]);

	const recordQuery = useCallback(() => {
		if (showSearchResult) return;

		return makeRecord({
			Action: 'S',
			Keyword: searchTerm,
		});
	}, [makeRecord, searchTerm, showSearchResult]);

	const toggleQuery = useCallback(() => {
		setShowSearchResult(!showSearchResult);
		recordQuery();
	}, [recordQuery, showSearchResult]);

	const chooseProduct = useCallback((relatedExhibitor, index) => {
		const product = relatedExhibitor.products[index];

		setSelectedExhibitor(relatedExhibitor['攤位號碼']);
		setSelectedProduct(product);
		setActiveFloor(relatedExhibitor.floor);
		setSelectedBooth(relatedExhibitor['攤位號碼']);
		toggleExhibitorInfo();
	}, [setActiveFloor, setSelectedBooth, setSelectedExhibitor, setSelectedProduct, toggleExhibitorInfo]);

	const selectAllCategories = useCallback(() => {
		const updatedCategories = [...categories];

		updatedCategories.forEach((category) => {
			category.level2.forEach((level2) => {
				level2.active = true;
				level2.level3.forEach((level3) => {
					level3.active = true;
				});
			});
		});

		setCategories(updatedCategories);
	}, [categories]);

	const clearAllCategories = useCallback(() => {
		const updatedCategories = [...categories];

		updatedCategories.forEach((category) => {
			category.level2.forEach((level2) => {
				level2.active = false;
				level2.level3.forEach((level3) => {
					level3.active = false;
				});
			});
		});

		setCategories(updatedCategories);
	}, [categories]);

	const recordExhibitorClicked = useCallback((booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
		});
	}, [makeRecord]);

	const recordProductClicked = useCallback((product, booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
			Product: product['產品名稱-中文'],
		});
	}, [makeRecord]);

	return (
		<div id='SearchPanel' className={`ui-panel booth ${SearchPanel ? 'active' : ''}`}>
			<div className='panel-heading'>{isCHT ? '進階搜尋' : 'Advanced Search'}</div>
			<div className='panel-body'>
				<div className='flex-controls'>
					<div className='search'>
						<input type='text' placeholder={placeholder} value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value) }} />
					</div>
					<div className='search-type'>
						<select value={searchType} onChange={(event) => {
							setSearchType(event.target.value);
						}}>
							<option value='company'>{isCHT ? '廠商' : 'Companies'}</option>
							<option value='product'>{isCHT ? '產品' : 'Products'}</option>
						</select>
					</div>
				</div>

				<div className='search-button' onClick={toggleQuery}>{showSearchResult ? (isCHT ? '重新查詢' : 'Refine Query') : (isCHT ? '送出查詢' : 'Send Query')}</div>

				{searchType === 'company' && showSearchResult ? (
					<div className='exhibitors'>
						{filteredExhibitorInfo.map((exhibitor, index) => {
							const exhibitorName = isCHT ? exhibitor['廠商名稱-中文'] : exhibitor['廠商名稱-英文'];
							const booth = exhibitor['攤位號碼'];

							return (
								<div className='exhibitor' key={`exhibitorLinkTableRow${index}`}>
									<div className='name' onClick={() => { chooseExhibitor(booth, exhibitor.floor); recordExhibitorClicked(booth); }}>{exhibitorName}</div>
									<div className='booth-number' onClick={() => { chooseExhibitor(booth, exhibitor.floor); recordExhibitorClicked(booth); }}>{booth}</div>
									<div className='icon' onClick={() => { toggleFavouriteExhibitor(booth) }}>
										{exhibitor.favourite ? <IconHeartFilled /> : <IconHeart />}
									</div>
								</div>
							)
						})}
					</div>
				) : null}

				{searchType === 'company' && showSearchResult && !filteredExhibitorInfo.length ? (
					<div className='no-result'>{isCHT ? '此搜尋無符合資料' : 'No matching result'}</div>
				) : null}

				{searchType === 'product' && showSearchResult ? (
					<div className='products'>
						{filteredProductInfo.map((product, i) => {
							const exhibitor = exhibitorInfo.find((exhibitor) => exhibitor['攤位號碼'] === product['攤位號碼']);

							return (
								<div className='product-wrap' key={`product-${i}`} onClick={() => { chooseProduct(exhibitor, i, product); recordProductClicked(product, exhibitor['攤位號碼']); }}>
									<div className='product-image' style={{ backgroundImage: `url(./product-photos/${product['產品圖']})` }}></div>
									<div className='product-name'>{product[`產品名稱-${isCHT ? '中文' : '英文'}`]}</div>
								</div>
							)
						})}
					</div>
				) : null}

				{searchType === 'product' && showSearchResult && !filteredProductInfo.length ? (
					<div className='no-result'>{isCHT ? '此搜尋無符合資料' : 'No matching result'}</div>
				) : null}

				{!showSearchResult ? (
					<div>
						<div className='flex-end'>
							<div className='button' onClick={selectAllCategories}>{isCHT ? '全選' : 'Select All'}</div>
							<div className='button' onClick={clearAllCategories}>{isCHT ? '清空' : 'Clear All'}</div>
						</div>

						{categories.map((category, index) => {
							return (
								<div className='category' key={`category${index}`}>

									<div className='name-level1'>{isCHT ? category.nameCHT : category.nameENG}</div>
									<div className='level2'>
										{category.level2.map((level2, inde) => {
											return (
												<SlideToggle collapsed key={`level2${inde}`}>
													{({ toggle, setCollapsibleElement }) => (
														<div className='level2-item'>
															<div className='flex-control'>
																<div className='check-wrap' onClick={() => {
																	toggleLevel2Check({
																		categoryIndex: index,
																		level2Index: inde,
																	});
																}}>
																	<div className={`checkbox ${level2.active ? 'active' : ''}`}></div>
																	<div className='name'>{isCHT ? level2.nameCHT : level2.nameENG}</div>
																</div>
																<IconArrowDown onClick={toggle} />
															</div>

															<div className='level3' ref={setCollapsibleElement}>
																{level2.level3.map((level3, ind) => {
																	return (
																		<div className='level3-item' key={`level3${ind}`}>
																			<div className='check-wrap' onClick={() => {
																				toggleLevel3Check({
																					categoryIndex: index,
																					level2Index: inde,
																					level3Index: ind,
																				})
																			}}>
																				<div className={`checkbox ${level3.active ? 'active' : ''}`}></div>
																				<div className='name'>{isCHT ? level3.nameCHT : level3.nameENG}</div>
																			</div>
																		</div>
																	)
																})}
																<div className='pad'></div>
															</div>
														</div>
													)}
												</SlideToggle>
											)
										})}
									</div>
								</div>
							)
						})}
					</div>
				) : null}

			</div>

			<IconClose onClick={closePanel} />
		</div>
	)
}

export default SearchPanel;